/* Pinterest saving, initiated only by a visitor. Ordinary links remain usable
   without an SDK; no image is uploaded and no Pin is created by this script. */
(() => {
  'use strict';
  if (window.byardsPinterestReady) return;
  const config = document.getElementById('byards-pinterest-config');
  const main = document.getElementById('main-content');
  const template = document.getElementById('byards-pinterest-button');
  if (!config || !main || !template) return;
  window.byardsPinterestReady = true;

  const selector = [
    '.bn-editorial-hero__media img', '.article__content img', '.bs-reading.article-content img',
    '.bs-opening__image img', '.card--article .card__media img', '.bs-journal-card__image img',
    '.bs-chapter__image img', '.bh-journal-card__visual img', '.bd-hero__mount img',
    '.bd-story__visual img', '.bls-hero__mount img', '.bdc-note__visual img',
    '.bh-feature__main img', '.bh-feature--christmas .bh-feature__visual img',
    '.bh-studio-card__visual img', '.bh-paper-edit__photograph img', '.bh-warmth__mount img',
    '.bh-department__image img', '.bh-christmas-invitation__card-image img',
    '.bls-design__image img', '.bdc-more__image img', '.media-with-text__media img',
    '[data-byards-pin-editorial] img'
  ].join(',');
  const exclude = [
    '[data-pin-nopin]', '[nopin]', '[data-byards-no-pin]', '[aria-hidden="true"]', '[hidden]',
    '.bn-editorial-logo', '.bs-story-mark', '.bs-ceo-portrait', 'media-gallery', 'product-card',
    'deferred-media', 'video', '.video-section', '.video-played-hidden', 'button', 'dialog', '[role="dialog"]',
    '[id*="customily" i]', '[class*="customily" i]'
  ].join(',');
  const records = new Set();
  const attachedImages = new WeakSet();
  const attachedButtons = new WeakSet();
  let layoutPending = false;
  let scanPending = false;

  function webUrl(value) {
    if (!value) return null;
    try {
      const url = new URL(value, config.dataset.shopUrl || location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url : null;
    } catch (_) { return null; }
  }

  function canonical(value) {
    const url = webUrl(value);
    if (!url) return '';
    url.pathname = url.pathname.replace(/\/collections\/[^/]+\/products\//, '/products/');
    url.search = '';
    url.hash = '';
    return url.href;
  }

  function fullImage(value) {
    const url = webUrl(value);
    if (!url) return '';
    const shop = webUrl(config.dataset.shopUrl);
    if (url.hostname === 'cdn.shopify.com' ||
        (shop && url.hostname === shop.hostname && url.pathname.startsWith('/cdn/'))) {
      ['width', 'height', 'crop'].forEach(key => url.searchParams.delete(key));
    }
    return url.href;
  }

  function largestSource(srcset) {
    return (srcset || '').split(',').map(part => {
      const [url, descriptor = '1x'] = part.trim().split(/\s+/);
      return { url, size: parseFloat(descriptor) || 0 };
    }).filter(part => webUrl(part.url)).sort((a, b) => b.size - a.size)[0]?.url || '';
  }

  function imageMedia(image) {
    // A mobile picture source may be different artwork, not just a smaller file.
    const picture = image.closest('picture');
    if (picture) {
      const source = Array.from(picture.querySelectorAll('source[srcset]')).find(item =>
        !item.media || window.matchMedia(item.media).matches);
      if (source) return fullImage(largestSource(source.srcset));
    }
    return fullImage(image.dataset.byardsPinMedia || largestSource(image.srcset) || image.currentSrc || image.src);
  }

  function description(title, alt) {
    const parts = [title, alt].map(value => (value || '').replace(/\s+/g, ' ').trim()).filter(Boolean);
    const unique = parts.filter((part, index) => parts.findIndex(other => other.toLowerCase() === part.toLowerCase()) === index);
    if (!unique.some(part => /byard[’']?s boutique/i.test(part))) unique.push('Byard’s Boutique');
    return unique.join(' — ').slice(0, 500);
  }

  function setLink(button, destination, media, title, alt) {
    const url = canonical(destination);
    if (!url || !webUrl(media)) return false;
    const copy = description(title, alt);
    const link = new URL('https://www.pinterest.com/pin/create/button/');
    link.searchParams.set('url', url);
    link.searchParams.set('media', media);
    link.searchParams.set('description', copy);
    button.href = link.href;
    button.dataset.pinUrl = url;
    button.dataset.pinMedia = media;
    button.dataset.pinDescription = copy;
    button.setAttribute('aria-label', `Save ${title || 'this photograph'} to Pinterest (opens in a new tab)`);
    return true;
  }

  function visible(image) {
    if (!image || image.closest('[hidden], [aria-hidden="true"]')) return false;
    const style = getComputedStyle(image);
    const rect = image.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' &&
      Number(style.opacity) > 0 && rect.width > 0 && rect.height > 0;
  }

  function visibleRect(image) {
    const box = image.getBoundingClientRect();
    const rect = { left: box.left, right: box.right, top: box.top, bottom: box.bottom };
    // Respect cropped and transformed photography as well as carousel viewports.
    for (let parent = image.parentElement; parent && main.contains(parent); parent = parent.parentElement) {
      const style = getComputedStyle(parent);
      if (style.visibility === 'hidden' || Number(style.opacity) === 0) return { ...rect, width: 0, height: 0 };
      const bounds = parent.getBoundingClientRect();
      if (/hidden|clip|scroll|auto/.test(style.overflowX)) {
        rect.left = Math.max(rect.left, bounds.left);
        rect.right = Math.min(rect.right, bounds.right);
      }
      if (/hidden|clip|scroll|auto/.test(style.overflowY)) {
        rect.top = Math.max(rect.top, bounds.top);
        rect.bottom = Math.min(rect.bottom, bounds.bottom);
      }
    }
    return { ...rect, width: Math.max(0, rect.right - rect.left), height: Math.max(0, rect.bottom - rect.top) };
  }

  function cardImage(button) {
    const host = button.closest('.card__media');
    if (!host) return null;
    const active = host.querySelector('.slider__item[aria-current="true"] img');
    if (visible(active)) return active;
    const hover = host.querySelector('.card__hover-image:not([hidden])');
    if (visible(hover) && Number(getComputedStyle(hover).opacity) > .5) return hover;
    return Array.from(host.querySelectorAll('img.card__main-image, .slider__item img'))
      .filter(visible).map(image => { const rect = visibleRect(image); return { image, area: rect.width * rect.height }; })
      .filter(item => item.area > 0).sort((a, b) => b.area - a.area)[0]?.image || null;
  }

  function attachButton(button) {
    if (attachedButtons.has(button)) return;
    attachedButtons.add(button);
    // Keep Save taps out of theme drag, zoom and card-navigation handlers.
    ['pointerdown', 'touchstart'].forEach(name => button.addEventListener(name, event => event.stopPropagation(), { passive: true }));
    const update = () => {
      if (button.dataset.byardsPinSave !== 'card') return;
      const image = cardImage(button);
      if (image) setLink(button, button.dataset.pinUrl, imageMedia(image), button.dataset.pinTitle, image.alt);
    };
    button.addEventListener('focus', update);
    button.addEventListener('pointerenter', update);
    button.addEventListener('click', event => {
      update();
      event.stopPropagation();
      // The normal target=_blank link opens Pinterest. Never save on the visitor's behalf.
    });
  }

  function editorialDestination(image) {
    const shop = webUrl(config.dataset.shopUrl);
    const valid = link => {
      const url = webUrl(link?.getAttribute('href'));
      return url && shop && url.hostname === shop.hostname &&
        /\/(?:products|collections|blogs|pages)\//.test(url.pathname) ? canonical(url.href) : '';
    };
    const enclosing = valid(image.closest('a[href]'));
    if (enclosing) return enclosing;
    const explicit = image.closest('[data-byards-pin-url]')?.dataset.byardsPinUrl;
    if (explicit) {
      const url = webUrl(explicit);
      if (url && shop && url.hostname === shop.hostname) return canonical(url.href);
    }
    const feature = image.closest('.bh-feature, .bh-studio-card, .bh-paper-edit, .bh-warmth, .card--article');
    if (feature) {
      for (const link of feature.querySelectorAll('a[href]:not([data-byards-pin-save])')) {
        const destination = valid(link);
        if (destination) return destination;
      }
    }
    return canonical(config.dataset.pageUrl);
  }

  function editorialTitle(image) {
    const card = image.closest('.card--article, .bh-journal-card, .bs-journal-card, .bh-department, .bh-christmas-invitation__card, .bls-design, .bdc-more__card, .bh-studio-card');
    return card?.querySelector('h2, h3, .card__title')?.textContent.trim() || image.alt || config.dataset.pageTitle;
  }

  function safeHost(image) {
    const link = image.closest('a');
    let host = link ? link.parentElement : image.parentElement;
    if (host?.tagName === 'PICTURE') host = host.parentElement;
    if (!host || !main.contains(host) || host.closest('a, button')) return null;
    if (getComputedStyle(image).position === 'absolute') {
      // Do not give an absolutely positioned image a new containing block.
      while (host && host !== main && getComputedStyle(host).position === 'static') host = host.parentElement;
      if (!host || getComputedStyle(host).position === 'static' || host.closest('a, button')) return null;
    } else if (getComputedStyle(host).position === 'static') {
      host.classList.add('byards-pin-positioned');
    }
    return host;
  }

  function position(record) {
    const { image, host, button } = record;
    if (!image.isConnected || !host.isConnected) {
      button.remove();
      resizeObserver?.unobserve(image);
      records.delete(record);
      return;
    }
    const rect = visibleRect(image);
    if (!visible(image) || rect.width < 120 || rect.height < 120) {
      button.hidden = true;
      return;
    }
    const parent = host.getBoundingClientRect();
    const scaleX = parent.width / host.offsetWidth || 1;
    const scaleY = parent.height / host.offsetHeight || 1;
    button.hidden = false;
    // Match the compact top-left controls used on product photographs.
    button.style.left = `${(rect.left - parent.left) / scaleX + host.scrollLeft - host.clientLeft + 10}px`;
    button.style.top = `${(rect.top - parent.top) / scaleY + host.scrollTop - host.clientTop + 10}px`;
    setLink(button, editorialDestination(image), imageMedia(image), editorialTitle(image), image.alt);
  }

  function scheduleLayout() {
    if (layoutPending) return;
    layoutPending = true;
    requestAnimationFrame(() => { layoutPending = false; records.forEach(position); });
  }

  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleLayout) : null;
  function addEditorial(image) {
    if (attachedImages.has(image) || image.closest(exclude) || !imageMedia(image)) return;
    const host = safeHost(image);
    if (!host) return;
    const button = template.content.firstElementChild.cloneNode(true);
    attachedImages.add(image);
    host.appendChild(button);
    attachButton(button);
    const record = { image, host, button };
    records.add(record);
    const hoverTarget = image.closest('a') || image.parentElement;
    hoverTarget.addEventListener('pointerenter', () => button.classList.add('is-active'));
    hoverTarget.addEventListener('pointerleave', event => {
      if (!button.contains(event.relatedTarget)) button.classList.remove('is-active');
    });
    button.addEventListener('pointerenter', () => button.classList.add('is-active'));
    button.addEventListener('pointerleave', () => button.classList.remove('is-active'));
    image.addEventListener('load', scheduleLayout);
    resizeObserver?.observe(image);
    position(record);
  }

  function scan() {
    scanPending = false;
    main.querySelectorAll('[data-byards-pin-save]').forEach(attachButton);
    if (config.dataset.editorial === 'true') main.querySelectorAll(selector).forEach(addEditorial);
    scheduleLayout();
  }
  function scheduleScan() {
    if (scanPending) return;
    scanPending = true;
    requestAnimationFrame(scan);
  }
  new MutationObserver(changes => {
    if (changes.some(change => !change.target.closest?.('[data-byards-pin-save]'))) scheduleScan();
  }).observe(main, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['hidden', 'aria-hidden', 'src', 'srcset']
  });
  document.addEventListener('shopify:section:load', scheduleScan);
  document.addEventListener('on:variant:change', scheduleScan);
  window.addEventListener('resize', scheduleLayout, { passive: true });
  main.addEventListener('transitionend', event => {
    if (!event.target.matches('[data-byards-pin-save]') && ['opacity', 'transform'].includes(event.propertyName)) scheduleLayout();
  });
  // Includes carousel scrolling inside main, without interfering with its handlers.
  main.addEventListener('scroll', scheduleLayout, { passive: true, capture: true });
  scan();
})();
